// GlobalSearch.jsx — ⌘K-style universal search across recipes, beans, pantry, favorites.
import { useState, useEffect, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, BookOpen, Coffee as CoffeeIcon, Package, Star } from "lucide-react";

export function GlobalSearch({ open, onClose, store, onNavigate }) {
  const [query, setQuery] = useState("");
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQuery("");
      setSelectedIdx(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const out = [];

    for (const r of (store.recipes || [])) {
      if (matches(q, r.title, r.summary, ...(r.cuisine || []), ...(r.mood_tags || []))) {
        out.push({ type: "recipe", id: r.id, title: r.title, sub: (r.cuisine || []).join(", "), data: r });
      }
    }

    for (const b of (store.beans || [])) {
      if (matches(q, b.name, b.roaster, ...(b.origin || []))) {
        out.push({ type: "bean", id: b.id, title: b.name, sub: b.roaster || (b.origin || []).join(", "), data: b });
      }
    }

    for (const s of (store.skus || [])) {
      if (matches(q, s.name, s.brand, s.category)) {
        out.push({ type: "sku", id: s.id, title: s.name, sub: s.brand || s.category, data: s });
      }
    }

    for (const f of (store.favorites || [])) {
      if (matches(q, f.name, f.notes)) {
        out.push({ type: "favorite", id: f.id, title: f.name, sub: `${f.profile_id} · ${f.water_ml}g`, data: f });
      }
    }

    return out.slice(0, 12);
  }, [query, store.recipes, store.beans, store.skus, store.favorites]);

  useEffect(() => {
    if (selectedIdx >= results.length) setSelectedIdx(Math.max(0, results.length - 1));
  }, [results.length, selectedIdx]);

  const handleSelect = (r) => {
    if (!r) return;
    const tabFor = { recipe: "recipes", bean: "coffee", sku: "pantry", favorite: "coffee" };
    onNavigate(tabFor[r.type] || "daily");
    onClose();
  };

  const handleKey = (e) => {
    if (e.key === "Escape") onClose();
    if (e.key === "ArrowDown") { e.preventDefault(); setSelectedIdx(i => Math.min(results.length - 1, i + 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setSelectedIdx(i => Math.max(0, i - 1)); }
    if (e.key === "Enter") { e.preventDefault(); handleSelect(results[selectedIdx]); }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/30 backdrop-blur-[2px] flex items-start justify-center pt-[10vh] px-4 font-sf"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: -16, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -16, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label="Search Pronto"
          className="w-full max-w-xl bg-pronto-page/90 backdrop-blur-[30px] border border-[var(--pg-border)] rounded-[20px] shadow-pronto-hero overflow-hidden"
        >
          <div className="flex items-center gap-3 px-4 py-3 border-b-[0.5px] border-[var(--p-sep)]">
            <Search size={17} className="text-pronto-text-3 flex-shrink-0" aria-hidden="true" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Search recipes, beans, pantry, favorites…"
              aria-label="Search Pronto"
              className="flex-1 bg-transparent font-sf text-[16px] text-pronto-ink placeholder:text-pronto-text-3 focus:outline-none"
            />
            <button
              onClick={onClose}
              aria-label="Close search"
              className="w-7 h-7 rounded-full grid place-items-center bg-[var(--pg-7)] border border-[var(--p-sep-12)] text-pronto-text-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pronto-navy"
            >
              <X size={14} />
            </button>
          </div>

          <div className="max-h-[60vh] overflow-y-auto">
            {results.length === 0 && query.trim() && (
              <div className="px-6 py-10 text-center">
                <div className="font-round font-semibold text-[16px] text-pronto-ink">Niente</div>
                <div className="font-sf text-[13px] text-pronto-text-2 mt-1">
                  Nothing matches “{query.trim()}”. Try fewer or different words.
                </div>
              </div>
            )}
            {results.length === 0 && !query.trim() && (
              <div className="px-6 py-10 text-center">
                <div className="font-sf text-[13.5px] text-pronto-text-2">
                  Search everything in Pronto — recipes, beans, pantry, favorites.
                </div>
                <div className="font-sf text-[11px] font-bold uppercase tracking-[0.1em] text-pronto-text-3 mt-2.5">
                  ⌘K to open · Esc to close
                </div>
              </div>
            )}
            {/* iOS inset-grouped rows: the hairline starts at the label, not the icon. */}
            {results.map((r, i) => {
              const Icon = iconFor(r.type);
              const on = selectedIdx === i;
              return (
                <button
                  key={`${r.type}-${r.id}`}
                  onClick={() => handleSelect(r)}
                  onMouseEnter={() => setSelectedIdx(i)}
                  aria-label={`${TYPE_LABEL[r.type] || r.type}: ${r.title}${r.sub ? `, ${r.sub}` : ""}`}
                  className={`w-full flex items-stretch gap-3 pl-4 text-left ${on ? "bg-[rgba(63,74,99,0.10)]" : ""}`}
                >
                  <span className="flex items-center flex-shrink-0">
                    <span className={`w-[26px] h-[26px] rounded-[7px] grid place-items-center text-white ${on ? "bg-pronto-navy" : "bg-pronto-text-2"}`}>
                      <Icon size={14} aria-hidden="true" />
                    </span>
                  </span>
                  <span className={`flex-1 min-w-0 flex items-center gap-3 pr-4 py-2.5 border-b-[0.5px] border-[var(--p-sep)] ${i === results.length - 1 ? "border-b-0" : ""}`}>
                    <span className="flex-1 min-w-0">
                      <span className="block font-sf text-[15px] text-pronto-ink truncate">{r.title}</span>
                      {r.sub && <span className="block font-sf text-[12px] text-pronto-text-2 truncate">{r.sub}</span>}
                    </span>
                    <span className="font-sf text-[10.5px] font-bold uppercase tracking-[0.08em] text-pronto-text-3 flex-shrink-0">
                      {TYPE_LABEL[r.type] || r.type}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

const TYPE_LABEL = {
  recipe: "recipe",
  bean: "bean",
  sku: "pantry",
  favorite: "favorite",
};

function matches(q, ...fields) {
  return fields.some(f => f && String(f).toLowerCase().includes(q));
}

function iconFor(type) {
  return type === "recipe" ? BookOpen
    : type === "bean" ? CoffeeIcon
    : type === "sku" ? Package
    : type === "favorite" ? Star
    : Search;
}
